import { ComponentRef } from '@angular/core';
import { ModalService } from '@ipreo/ngx-sprinkles';

/**
 * Normalises options before passing them to ModalService.create().
 *
 * Two bugs exist in the upgraded ngx-sprinkles v21 ModalService that this
 * helper works around:
 *
 * 1. `width` is missing from the internal `flatProps` list inside
 *    `assignDefaults()`, so a flat `width` value is only applied via a
 *    fall-through path that guards with `hasOwnProperty()`.  In Angular 21
 *    Ivy-compiled components the property may not be own-enumerable until
 *    written, so the value is silently dropped.  Moving `width` (and any
 *    other non-listed property) into `inputs:{}` ensures it always reaches
 *    the component instance.
 *
 * 2. The ModalComponent template now uses `[ngStyle]="{ width, minWidth,
 *    height }"` instead of the old `[style.width.px]` binding.  ngStyle
 *    does NOT append "px" to plain numbers, so numeric values are treated
 *    as invalid and ignored by the browser.  This helper converts numbers
 *    to CSS pixel strings before they reach ngStyle.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createModal(
  modalService: ModalService,
  options: Record<string, any>
): ComponentRef<any> {
  const { width, height, inputs: extraInputs, ...rest } = options;

  const resolvedInputs: Record<string, any> = { ...extraInputs };

  if (width !== undefined) {
    resolvedInputs['width'] = typeof width === 'number' ? `${width}px` : width;
  }

  if (height !== undefined) {
    resolvedInputs['height'] =
      typeof height === 'number' ? `${height}px` : height;
  }

  return modalService.create({
    ...rest,
    inputs: resolvedInputs
  });
}
